import axios from "axios";
import { env } from "../env";
import { store } from "redux/store";
import { setIsLogin, setTokenisedId } from "redux/signPassIdentify";
import { changeIsLoading } from "redux/loading";
import { identity } from "lodash";
import { updateError } from "redux/o2Appointment";

const ERROR = "error";

axios.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    store.dispatch(changeIsLoading(true));
    config.withCredentials = true;
    return config;
  },
  function (error) {
    // Do something with request error
    store.dispatch(changeIsLoading(false));
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    store.dispatch(changeIsLoading(false));
    if (response?.data?.status === ERROR) {
      store.dispatch(updateError("Unexpected error has occurred. Please try again later."));
      response.status = 500;
    }
    return response;
  },
  function (error) {
    store.dispatch(changeIsLoading(false));
    store.dispatch(updateError("Unexpected error has occurred. Please try again later."));

    if (error?.response?.status === 401) {
      store.dispatch(setIsLogin(false));
      store.dispatch(setTokenisedId(null));
      return error.response;
    }
    // if (error.response.status === 500) return error.response;

    return Promise.reject(error);
  }
);

export async function loadQueue({ nric, dept }) {
  return await axios.get(`${env.apiUrl}/public/queue/getInfo/${nric}/${dept}`);
}

export async function regQueue({ nric, mobileNumber, dept }) {
  return await axios.get(`${env.apiUrl}/public/queue/registerFlow/${nric}/${mobileNumber}/${dept}`);
}

export async function sendSms({ nric, mobileNumber }) {
  return await axios.post(`${env.apiUrl}/public/queue`, { nric, mobileNumber });
}

export async function validateOtp(nric, mobile, otp) {
  return await axios.post(`${env.apiUrl}/public/otp/validate`, {
    nric,
    mobile,
    otp
  });
}

export async function getOtp(nric, mobileNumber) {
  return await axios.post(`${env.apiUrl}/public/otp/get`, {
    nric,
    mobile: mobileNumber
  });
}

export async function validateOtpToken(otpToken) {
  let result = null;
  try {
    result = await axios.get(
      `${env.apiUrl}/api/oas/otp/validateToken?otpToken=${encodeURIComponent(otpToken)}`
    );
    if (result.status === 401) {
      return { status: "NO_AUTH" };
    }
    return result.data.data;
  } catch (e) {
    return { status: "ERROR" };
  }
}

export async function validateQr({ key }) {
  return await axios.get(`${env.apiUrl}/public/qr/validate/${key}`);
}

export async function getDept({ deptId }) {
  return await axios.get(`${env.apiUrl}/public/settings/dept/${deptId}`);
}

export async function getSetting({ deptId }) {
  return await axios.get(`${env.apiUrl}/public/settings/setting/${deptId}`);
}

export async function getService() {
  return await axios.get(`${env.apiUrl}/api/oas/main-service`);
}

export async function getSubService(mainServiceId) {
  return await axios.get(`${env.apiUrl}/api/oas/main-service/${mainServiceId}`);
}

export async function getServiceLinks(mainServiceId, subServiceId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/service-links/main-service/${mainServiceId}/sub-service/${subServiceId}`
  );
}

export async function getAppointment() {
  return await axios.get(`${env.apiUrl}/api/oas/appointment`);
}

export async function getServiceChannels(age, subServiceId, isCrps) {
  return await axios.get(
    `${env.apiUrl}/api/oas/service-channels?age=${age}&subServiceId=${subServiceId}&isCrps=${isCrps}`
  );
}

export async function getOtherChannels(subServiceId) {
  return await axios.get(`${env.apiUrl}/api/oas/other-channels?subServiceId=${subServiceId}`);
}

export async function getAppointmentBooking(appointmentTypeId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/appointment-booking?appTypeId=${appointmentTypeId}`
  );
}

export async function getTimeSlots(date, locationId, appointmentTypeId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/timeslots?date=${date}&locationId=${locationId}&appTypeId=${appointmentTypeId}`
  );
}

export async function getSuggestedTimeSlots(appointmentTypeId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/appointment-booking-suggested-slot-day?appTypeId=${appointmentTypeId}`
  );
}

export async function getTimeSlotsWithoutLocationId(date, appointmentTypeId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/timeslots-without-location?date=${date}&appTypeId=${appointmentTypeId}`
  );
}

export async function appointmentBookingLocks(
  appointmentLockInfo,
  attendingWithAnother,
  appointmentBookingId
) {
  return await axios.post(`${env.apiUrl}/api/oas/appointment-booking-locks`, {
    appointmentLockInfo,
    attendingWithAnotherPerson: attendingWithAnother,
    appointmentBookingId
  });
}

export async function appointmentBooking(
  serviceId,
  subServiceId,
  enquiryForId,
  visitorIdentity,
  birthYear,
  preferLang,
  otherEnquiryServiceId,
  contactNo,
  apptTypeId,
  attendingWithAnotherPerson,
  locationId,
  appointmentDt,
  liveApptTimeslotId,
  anotherPersonIdentity,
  anotherLiveApptTimeslotId,
  anotherAppointmentDt,
  location
) {
  return await axios.post(`${env.apiUrl}/api/oas/appointment-bookings`, {
    serviceId,
    subServiceId,
    enquiryForId,
    visitorIdentity,
    birthYear,
    preferLang,
    otherEnquiryServiceId,
    contactNo,
    apptTypeId,
    attendingWithAnotherPerson,
    locationId,
    appointmentDt,
    liveApptTimeslotId,
    anotherPersonIdentity,
    anotherLiveApptTimeslotId,
    anotherAppointmentDt,
    location
  });
}

export async function cancelAppointmentBooking(
  appointmentId,
  visitorIdentity,
  liveApptTimeslotId,
  attendingWithAnotherPerson,
  contactNo,
  appointmentDt,
  location
) {
  return await axios.post(`${env.apiUrl}/api/oas/appointment-bookings/cancellation`, {
    appointmentId,
    visitorIdentity,
    liveApptTimeslotId,
    attendingWithAnotherPerson,
    contactNo,
    appointmentDt,
    location: location || ""
  });
}

export async function changeAppointmentBooking(
  serviceId,
  subServiceId,
  enquiryForId,
  visitorIdentity,
  birthYear,
  preferLang,
  otherEnquiryServiceId,
  contactNo,
  apptTypeId,
  attendingWithAnotherPerson,
  locationId,
  appointmentDt,
  liveApptTimeslotId,
  previousBookingId,
  previousLiveAppTimeslotId,
  anotherPersonIdentity,
  anotherAppointmentDt,
  anotherLiveApptTimeslotId,
  bookingTxnId
) {
  return await axios.post(`${env.apiUrl}/api/oas/appointment-bookings/change`, {
    serviceId,
    subServiceId,
    enquiryForId,
    visitorIdentity,
    birthYear,
    preferLang,
    otherEnquiryServiceId,
    contactNo,
    apptTypeId,
    attendingWithAnotherPerson,
    locationId,
    appointmentDt,
    liveApptTimeslotId,
    previousBookingId,
    previousLiveAppTimeslotId,
    anotherPersonIdentity,
    anotherAppointmentDt,
    anotherLiveApptTimeslotId,
    bookingTxnId
  });
}

export async function existedAppointmentBooking(visitorIdentity) {
  return await axios.post(`${env.apiUrl}/api/oas/appointment-bookings/existed`, {
    identity: visitorIdentity
  });
}

export async function resendOTP(nric, mobile) {
  return await axios.post(`${env.apiUrl}/api/oas/otp/get`, {
    nric,
    mobile
  });
}

export async function verifyOTP(nric, mobile, otp) {
  return await axios.post(`${env.apiUrl}/api/oas/otp/validate`, {
    nric,
    mobile,
    otp
  });
}

export async function validateCaptcha(token) {
  return await axios.post(`${env.apiUrl}/api/oas/captchaValidate`, {
    token
  });
}

export async function appointmentTakenUp(appTypeId, date) {
  return await axios.get(
    `${env.apiUrl}/api/oas/appointment-taken-up?date=${date}&appTypeId=${appTypeId}`
  );
}

export async function getBannerText() {
  return await axios.get(`${env.apiUrl}/api/oas/banner-text`);
}

export async function getAppointmentAvailableDates(apptTypeId, locationId) {
  return await axios.get(
    `${env.apiUrl}/api/oas/appointment-available-dates?apptTypeId=${apptTypeId}${
      locationId ? `&locationId=${locationId}` : ""
    }`
  );
}

export async function getCrpsDob() {
  return await axios.get(`${env.apiUrl}/api/oas/crps-dob`);
}

//region PROJECT X
/**
 * @returns {Promise<"SUCCESS" | "ERROR">}
 */
export async function getOtpEmail({ passport, email, captchaResponseToken }) {
  let result = null;
  try {
    // { status : "SUCCESS" | "ERROR" } | NET_ERROR
    result = await axios.post(`${env.apiUrl}/api/oas/otp/getEmail`, {
      passport,
      email,
      captchaResponseToken
    });

    const { status } = result.data || {};
    return status;
  } catch (e) {
    return "ERROR";
  }
}

/**
 * @returns {Promise<{status: "SUCCESS" | "ERROR"}, data: {validation: true|false, otpToken: "xxx"}>}
 */
export async function verifyOTPEmail({ passport, email, otp }) {
  let result = null;
  try {
    // {status:"SUCCESS", data: {validation: true|false, otpToken: "xxx"}} | NET_ERROR
    result = await axios.post(`${env.apiUrl}/api/oas/otp/validateEmail`, {
      passport,
      email,
      otp
    });

    return result.data;
  } catch (e) {
    return { status: "ERROR" };
  }
}

export async function getAppointmentAvailableDates2(locationId, apptTypeId) {
  let result = null;
  try {
    result = await axios.get(
      `${env.apiUrl}/api/oas/appointment-available-dates?apptTypeId=${apptTypeId}${
        locationId ? `&locationId=${locationId}` : ""
      }`
    );
    if(result.data.status === "error") {
      return { status: "ERROR" };
    }
    return { status: "SUCCESS", data: result.data.data };
  } catch (e) {
    return { status: "ERROR" };
  }
}

/**
 *
 * @returns {Promise<{data, status: string}|{status: string}>}
 */
export async function getTimeSlotsX(date, locationId, appointmentTypeId, otpToken) {
  let result = null;
  try {
    result = await axios.get(
      `${
        env.apiUrl
      }/api/oas/timeslotsx?date=${date}&locationId=${locationId}&appTypeId=${appointmentTypeId}&otpToken=${encodeURIComponent(
        otpToken
      )}`
    );
    if (result.status === 401) {
      return { status: "NO_AUTH" };
    }
    if(result.data.status === "error") {
      return { status: "ERROR" };
    }
  } catch (e) {
    return { status: "ERROR" };
  }
  return { status: "SUCCESS", data: result.data.data };
}

export async function createAppointmentBookingX({ timeslotId, name, email, passport, otpToken, idType }) {
  try {
    const result = await axios.post(`${env.apiUrl}/api/oas/appointment-bookingsx`, {
      timeslotId,
      name,
      email,
      passport,
      otpToken,
      idType
    });

    if (result.status === 401) {
      return { status: "NO_AUTH" };
    }
    // 500 in upstream
    if(result.data.status === "error") {
      return { status: result.data.message };
    }

    return result.data;
  } catch (e) {
    return { status: "ERROR" };
  }
}

export async function changeAppointmentBookingX({ timeslotId, name, email, passport, otpToken }) {
  try {
    const result = await axios.post(`${env.apiUrl}/api/oas/appointment-bookingsx/change`, {
      timeslotId,
      name,
      email,
      passport,
      otpToken
    });

    if (result.status === 401) {
      return { status: "NO_AUTH" };
    }
    // 500 in upstream
    if(result.data.status === "error") {
      return { status: result.data.message };
    }

    return result.data;
  } catch (e) {
    return { status: "ERROR" };
  }
}



/**
 * @returns {Promise<{data, status: string}|{status: string}>}
 */
export async function getExistingAppointmentX({ passport, otpToken }) {
  try {
    const result = await axios.post(`${env.apiUrl}/api/oas/appointment-bookings/existedx`, {
      identity: passport,
      otpToken
    });
    // Auth validation fail
    if(result.status === 401) {
      return {status: "NO_AUTH", data: result.data.message}
    }
    // 500 in upstream
    if(result.data.status === "error") {
      return { status: result.data.message };
    }
    return { status: "SUCCESS", data: result.data };
  } catch (e) {
    return { status: "ERROR" };
  }
}

export async function cancelAppointmentBookingX(
    {  email, visitorIdentity, appointmentId, liveApptTimeslotId, appointmentDt, otpToken }
) {
  try {
  let result = await axios.post(`${env.apiUrl}/api/oas/appointment-bookings/cancellationx`, {
      email, visitorIdentity, appointmentId, liveApptTimeslotId, appointmentDt, otpToken
  });
  // Auth validation fail
    if(result.status === 401) {
      return {status: "NO_AUTH"}
    }
    // 500 in upstream
    if(result.data.status === "error") {
      return { status: "ERROR" };
    }
    if(result.data.status) {
      return {status: "SUCCESS"}
    }

    return {status: "ERROR"};
  } catch(e) {
    return {status: "ERROR"};
  }
}

//endregion


